import { useEffect, useMemo, useRef, useState } from 'react';
import { LobbyTypeEnum, MarketStatus } from '@sportspredict/shared';
import { TABS } from '@/constants';
import { useGetHasSecondChanceTradesQuery } from '@/store/api';
import { getEliminationInfo, getUserLobbyEliminationRecord } from '@/utils';

export function useSurvivorSecondChanceTracking({
	isCrowdLockMode,
	isSecondChanceD2,
	d2Markets,
	userSecondChanceTrades,
	sortedMarkets,
	userTrades,
	lobbyId,
	activeTab,
	user,
	data,
	isSurvivorMode,
	slateUserTradeCounts,
	markets,
	lobbyResponse,
	isPoolingPhaseActive,
	showCrowdLockModal,
	showPasswordDialog,
	autoJoinPending,
	isJoining,
	showSecondChancePopup,
	setShowSecondChancePopup,
	setShowSecondChanceLockedPopup,
}: any) {
	const [showSurvivorPopup, setShowSurvivorPopup] = useState(false);
	const hasShownSurvivorRef = useRef(false);

	const hasCompletedAllSecondChanceTrades = useMemo(() => {
		if (!isCrowdLockMode) return false;

		if (isSecondChanceD2) {
			if (!d2Markets?.length) return false;
			return d2Markets.every((market: any) =>
				userSecondChanceTrades.some((t: any) => t.second_chance_market_id === market.id)
			);
		}

		const secondChanceMarkets = sortedMarkets.filter(
			(m: any) => m.status === MarketStatus.SecondChance
		);
		if (!secondChanceMarkets.length) return false;

		return secondChanceMarkets.every((market: any) =>
			userTrades.some((trade: any) => trade.market_id === market.id && trade.lobby_id === lobbyId)
		);
	}, [
		sortedMarkets,
		userTrades,
		lobbyId,
		isCrowdLockMode,
		isSecondChanceD2,
		d2Markets,
		userSecondChanceTrades,
	]);

	useEffect(() => {
		if (activeTab !== TABS.MARKETS) return;
		if (hasShownSurvivorRef.current) return;
		if (!user || !data.eventResponse || !data.lobbyResponse) return;

		if (isSurvivorMode) {
			setShowSurvivorPopup(true);
			hasShownSurvivorRef.current = true;
		}
	}, [activeTab, user, data.eventResponse, data.lobbyResponse, isSurvivorMode]);

	const userTradeCount = useMemo(() => {
		if (!slateUserTradeCounts || typeof slateUserTradeCounts !== 'object') {
			return 0;
		}

		const first = Object.values(slateUserTradeCounts)[0];
		return (first as any)?.total ?? 0;
	}, [slateUserTradeCounts]);

	const userCorrectTradeCount = useMemo(() => {
		if (!slateUserTradeCounts || typeof slateUserTradeCounts !== 'object') {
			return 0;
		}

		const first = Object.values(slateUserTradeCounts)[0];
		return (first as any)?.wins ?? 0;
	}, [slateUserTradeCounts]);

	const eliminationInfo = useMemo(
		() =>
			getEliminationInfo({
				userTrades,
				markets,
			}),
		[userTrades, markets]
	);

	const userEliminationInfoCrowdLock = useMemo(() => {
		if (!user?.id) {
			return { isUserEliminated: false, eliminatedRound: null };
		}
		if (!lobbyId) {
			return { isUserEliminated: false, eliminatedRound: null };
		}

		const record = getUserLobbyEliminationRecord(lobbyResponse, user.id, lobbyId);

		if (!record) {
			return { isUserEliminated: false, eliminatedRound: null };
		}

		return {
			isUserEliminated: true,
			eliminatedRound: isNaN(Number(record.round)) ? null : Number(record.round),
		};
	}, [lobbyResponse?.eliminated_users, lobbyId, user?.id]);

	const { isUserEliminated } = userEliminationInfoCrowdLock;

	const { data: secondChanceData, isSuccess: secondChanceLoaded } =
		useGetHasSecondChanceTradesQuery(
			{ lobbyId: lobbyResponse?.id },
			{
				skip: lobbyResponse?.type !== LobbyTypeEnum.CrowdLock,
			}
		);

	const hasSecondChanceTrades = secondChanceData?.hasSecondChanceTrades ?? false;

	const [showNoSecondChancePopup, setShowNoSecondChancePopup] = useState(false);

	const noSecondChancePopupKey =
		user?.id && lobbyId ? `no_second_chance_popup_shown_${user.id}_${lobbyId}` : null;

	useEffect(() => {
		if (!noSecondChancePopupKey) return;
		if (!secondChanceLoaded) return;

		if (
			lobbyResponse?.type !== LobbyTypeEnum.CrowdLock ||
			!isUserEliminated ||
			hasSecondChanceTrades ||
			isPoolingPhaseActive
		) {
			return;
		}

		const alreadyShown = sessionStorage.getItem(noSecondChancePopupKey);

		if (!alreadyShown) {
			setShowNoSecondChancePopup(true);
			sessionStorage.setItem(noSecondChancePopupKey, 'true');
		}
	}, [
		lobbyId,
		lobbyResponse?.type,
		isUserEliminated,
		hasSecondChanceTrades,
		isPoolingPhaseActive,
		noSecondChancePopupKey,
		secondChanceLoaded,
	]);

	const isSecondChanceMarkets = useMemo(() => {
		return (markets?.some((m: any) => m.is_second_chance) ?? false) || isSecondChanceD2;
	}, [markets, isSecondChanceD2]);
	// When revival is initiated, switch from Second Chance view to settled view
	const isSecondChance =
		isPoolingPhaseActive && isSecondChanceMarkets && !lobbyResponse?.is_revival_initiated;

	const secondChancePopupKey = `second_chance_popup_shown_${lobbyId}`;
	const secondChanceLockedPopupKey = `second_chance_locked_popup_shown_${lobbyId}`;

	const canSecondChancePopup =
		isSecondChance &&
		!hasCompletedAllSecondChanceTrades &&
		!showCrowdLockModal &&
		!showPasswordDialog &&
		!autoJoinPending &&
		!isJoining;

	useEffect(() => {
		if (!lobbyId) return;
		if (!isSecondChance || !hasCompletedAllSecondChanceTrades) return;

		const alreadyShown = sessionStorage.getItem(secondChanceLockedPopupKey);

		if (!alreadyShown) {
			setShowSecondChanceLockedPopup(true);
			sessionStorage.setItem(secondChanceLockedPopupKey, 'true');
		}
	}, [lobbyId, isSecondChance, hasCompletedAllSecondChanceTrades]);

	useEffect(() => {
		if (!lobbyId) return;
		if (showPasswordDialog || autoJoinPending || isJoining) return;
		if (!isPoolingPhaseActive || !isSecondChance) return;

		const alreadyShown = sessionStorage.getItem(secondChancePopupKey);

		if (!alreadyShown) {
			setShowSecondChancePopup(true);
			sessionStorage.setItem(secondChancePopupKey, 'true');
		}
	}, [
		lobbyId,
		isPoolingPhaseActive,
		isSecondChance,
		showPasswordDialog,
		autoJoinPending,
		isJoining,
	]);

	useEffect(() => {
		if (!showSecondChancePopup) return;
		if (!showPasswordDialog && !autoJoinPending && !isJoining) return;

		setShowSecondChancePopup(false);
	}, [showSecondChancePopup, showPasswordDialog, autoJoinPending, isJoining]);

	return {
		showSurvivorPopup,
		setShowSurvivorPopup,
		hasCompletedAllSecondChanceTrades,
		userTradeCount,
		userCorrectTradeCount,
		eliminationInfo,
		isUserEliminated,
		hasSecondChanceTrades,
		secondChanceLoaded,
		showNoSecondChancePopup,
		setShowNoSecondChancePopup,
		isSecondChance,
		canSecondChancePopup,
	};
}
